import { collection, onSnapshot, addDoc, serverTimestamp, query, orderBy, deleteDoc, doc, updateDoc, arrayUnion, getDocs } from "firebase/firestore";
import { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import Icon from "./Icon";
import SegmentCard from "./SegmentCard";
import AddSegmentModal from "./AddSegmentModal";
import ShareModal from "./ShareModal";
import ExportModal from "./ExportModal";
import WeatherStrip from "./WeatherStrip";
import { groupByDate, formatDate, formatDateFull, localDateStr, isUpcoming } from "../utils";
import EditTripModal from "./EditTripModal";
import MergeTripModal from "./MergeTripModal";

const WEATHER_API_KEY = process.env.REACT_APP_OPENWEATHER_API_KEY;

const weatherEmoji = (code) => {
  if (code >= 200 && code < 300) return "⛈";
  if (code >= 300 && code < 400) return "🌦";
  if (code >= 500 && code < 600) return "🌧";
  if (code >= 600 && code < 700) return "❄️";
  if (code >= 700 && code < 800) return "🌫";
  if (code === 800) return "☀️";
  if (code === 801) return "🌤";
  if (code <= 804) return "☁️";
  return "🌡";
};

export default function TripDetail({ trip, user, onBack, onSignOut, onLocalRestaurants }) {
  const [segments, setSegments] = useState([]);
  const [weatherCache, setWeatherCache] = useState({}); // city → { emoji, high, low, date }
  const [showAdd, setShowAdd] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [showEditTrip, setShowEditTrip] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [prefillDate, setPrefillDate] = useState("");
  const [prefillCity, setPrefillCity] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [notes, setNotes] = useState(trip.notes || "");
  const [editingNotes, setEditingNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [tripData, setTripData] = useState(trip);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [savedFaves, setSavedFaves] = useState({});
  const menuRef = useRef(null);
  const notesRef = useRef(null);
  const todayRef = useRef(null);
  const tripHeaderRef = useRef(null);

  // Is this a shared trip (more than one member)?
  const isSharedTrip = (tripData.memberIds?.length || 1) > 1;


  // Load savedRestaurants for persistent fave cross-referencing
  useEffect(() => {
    const loadSaved = async () => {
      try {
        const snap = await getDocs(
          collection(db, "users", user.uid, "savedRestaurants")
        );
        const map = {};
        snap.docs.forEach(d => {
          const data = d.data();
          if (data.title) map[data.title.toLowerCase()] = data;
        });
        setSavedFaves(map);
      } catch (e) {
        console.warn("savedFaves load failed:", e);
      }
    };
    loadSaved();
  }, [user.uid]);

  useEffect(() => {
    const q = query(
      collection(db, "trips", trip.id, "segments"),
      orderBy("date"), orderBy("time")
    );
    const unsub = onSnapshot(q, (snap) => {
      const segs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSegments(segs);
      // Keep nextSegment + upcomingSegments on trip doc in sync
      const TYPE_PRIORITY = { flight: 0, train: 1, car: 2, activity: 3, restaurant: 4, hotel: 5, other: 6 };
      const upcoming = segs
        .filter(s => s.date && isUpcoming(s) && !((s.hiddenFor || []).includes(user.uid)))
        .sort((a, b) => {
          const dateA = a.date + (a.time || "");
          const dateB = b.date + (b.time || "");
          if (dateA !== dateB) return dateA.localeCompare(dateB);
          return (TYPE_PRIORITY[a.type] ?? 6) - (TYPE_PRIORITY[b.type] ?? 6);
        });
      const brief = (s) => ({ type: s.type, title: s.title, date: s.date, time: s.time || "" });
      updateDoc(doc(db, "trips", trip.id), {
        nextSegment: upcoming[0] ? brief(upcoming[0]) : null,
        upcomingSegments: upcoming.slice(0, 5).map(brief),
      }).catch(() => {});
    });
    return unsub;
  }, [trip.id, user.uid]);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (editingNotes && notesRef.current) {
      notesRef.current.focus();
    }
  }, [editingNotes]);

  // Fetch weather for upcoming hotel/flight segments (next 5 days)
  useEffect(() => {
    if (!WEATHER_API_KEY || segments.length === 0) return;
    const today = localDateStr();
    const fiveDaysOut = localDateStr(new Date(Date.now() + 5 * 24 * 60 * 60 * 1000));

    const targets = segments.filter(s =>
      (s.type === "hotel" || s.type === "flight") &&
      s.date && s.date >= today && s.date <= fiveDaysOut &&
      s.destinationCity
    );

    const uniqueCities = [...new Map(targets.map(s => [
      s.destinationCity.toLowerCase(),
      {
        city: s.destinationCity,
        query: s.lat && s.lng
          ? `lat=${s.lat}&lon=${s.lng}`
          : `q=${encodeURIComponent(s.destinationCountryCode ? `${s.destinationCity},${s.destinationCountryCode}` : s.destinationCity)}`,
        date: s.date,
      }
    ])).values()];

    uniqueCities.forEach(async ({ city, query, date }) => {
      if (weatherCache[city.toLowerCase()]) return;
      try {
        // Use forecast endpoint to get the day's high/low
        const res = await fetch(
          `https://api.openweathermap.org/data/2.5/forecast?${query}&appid=${WEATHER_API_KEY}&units=imperial&cnt=40`
        );
        const data = await res.json();
        if (data.cod !== "200") return;
        // Find items for the segment's date
        const items = (data.list || []).filter(i => i.dt_txt.startsWith(date));
        if (items.length === 0) return; // date outside forecast window — don't show badge
        const high = Math.round(Math.max(...items.map(i => i.main.temp_max)));
        const low = Math.round(Math.min(...items.map(i => i.main.temp_min)));
        const noon = items.find(i => i.dt_txt?.includes("12:00")) || items[Math.floor(items.length / 2)];
        const emoji = noon ? weatherEmoji(noon.weather[0].id) : "🌡";
        setWeatherCache(prev => ({ ...prev, [city.toLowerCase()]: { emoji, high, low } }));
      } catch { /* silently skip */ }
    });
  }, [segments]); // eslint-disable-line react-hooks/exhaustive-deps

  // Measure the sticky trip header so the weather strip can stick right below it
  // (its height changes with notes, breadcrumb, and wrapping).
  useEffect(() => {
    const el = tripHeaderRef.current;
    if (!el) return;
    const root = document.documentElement;
    const update = () => root.style.setProperty("--trip-header-height", `${el.offsetHeight}px`);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      root.style.removeProperty("--trip-header-height");
    };
  }, []);

  // Scroll to today's date header (or the next date with segments) when segments load
  useEffect(() => {
    if (segments.length === 0) return;
    const go = () =>
      todayRef.current?.scrollIntoView({ behavior: "auto", block: "start" });
    requestAnimationFrame(go);
    const t = setTimeout(go, 500); // again once the weather strip has loaded
    return () => clearTimeout(t);
  }, [segments.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddSegment = async (segData) => {
    await addDoc(collection(db, "trips", trip.id, "segments"), {
      ...segData,
      createdAt: serverTimestamp(),
      createdBy: user.uid,
    });

    // Notify other trip members (fire and forget)
    fetch("/api/notifications?action=notify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tripId: trip.id,
        segment: segData,
        adderUid: user.uid,
        source: "manual",
      }),
    }).catch(() => {});
  };

  const handleToggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = () => {
    if (!selectedIds.size) return;
    if (isSharedTrip) {
      setShowBulkDeleteDialog(true);
    } else {
      if (!window.confirm(`Remove ${selectedIds.size} segment${selectedIds.size > 1 ? "s" : ""}?`)) return;
      executeBulkDelete();
    }
  };

  const executeBulkDelete = async () => {
    for (const id of selectedIds) {
      await deleteDoc(doc(db, "trips", trip.id, "segments", id));
    }
    setSelectedIds(new Set());
    setShowBulkDeleteDialog(false);
  };

  const executeBulkHide = async () => {
    for (const id of selectedIds) {
      await updateDoc(doc(db, "trips", trip.id, "segments", id), {
        hiddenFor: arrayUnion(user.uid),
      });
    }
    setSelectedIds(new Set());
    setShowBulkDeleteDialog(false);
  };

  const handleOpenAdd = (date) => {
    const d = date || "";
    setPrefillDate(d);
    setPrefillCity("");
    setShowAdd(true);
  };

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    await updateDoc(doc(db, "trips", trip.id), { notes });
    setSavingNotes(false);
    setEditingNotes(false);
  };

  // Filter out segments hidden by the current user
  const visibleSegments = segments.filter(
    (seg) => !(seg.hiddenFor || []).includes(user.uid)
  );

  const today = localDateStr();

  const grouped = groupByDate(visibleSegments);
  const startLabel = tripData.startDate ? formatDate(tripData.startDate) : "";
  const endLabel = tripData.endDate ? formatDate(tripData.endDate) : "";

  // Build city breadcrumb: home → unique cities in segment order
  const cityBreadcrumb = (() => {
    const seen = new Set();
    const cities = [];
    for (const seg of visibleSegments) {
      if (seg.type !== "hotel") continue;
      const c = (seg.city || "").trim();
      if (c && !seen.has(c)) { seen.add(c); cities.push(c); }
    }
    return cities.length ? ["Columbus", ...cities] : [];
  })();
  const memberCount = tripData.members?.length || 1;

  return (
    <>
      <header className="header">
        <button className="icon-btn" onClick={onBack}>
          <Icon name="back" size={18} />
        </button>
        <div className="header-logo" style={{ fontSize: 17 }}>Gulliver Travels</div>
        <div ref={menuRef} className="dropdown-wrapper">
          <button className="menu-btn" onClick={() => setShowMenu(!showMenu)}>
            •••
          </button>
          {showMenu && (
            <div className="dropdown">
              <button className="dropdown-item" onClick={() => { setShowEditTrip(true); setShowMenu(false); }}>
                <Icon name="edit" size={14} /> Edit trip
              </button>
              <button className="dropdown-item" onClick={() => { setShowMerge(true); setShowMenu(false); }}>
                🔀 Merge another trip in
              </button>
              <button className="dropdown-item" onClick={() => { setShowShare(true); setShowMenu(false); }}>
                <Icon name="share" size={14} /> Share trip
              </button>
              <button className="dropdown-item" onClick={() => { setShowExport(true); setShowMenu(false); }}>
                📤 Export itinerary
              </button>
              <div className="dropdown-divider" />
              <button className="dropdown-item danger" onClick={onSignOut}>
                <Icon name="signout" size={14} /> Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Trip sub-header */}
      <div className="trip-detail-header" ref={tripHeaderRef}>
        <div className="trip-detail-top">
          <div style={{ minWidth: 0 }}>
            <div className="trip-detail-title">{tripData.name}</div>
            <div className="trip-detail-meta">
              {startLabel && endLabel ? `${startLabel} — ${endLabel}` : "Dates TBD"}
              {" · "}
              {memberCount} traveler{memberCount !== 1 ? "s" : ""}
            </div>
            {cityBreadcrumb.length > 0 && (
              <div style={{
                marginTop: 6,
                fontSize: 12,
                color: "var(--ink-light)",
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 2,
              }}>
                {cityBreadcrumb.map((city, i) => (
                  <span key={city} style={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {i > 0 && <span style={{ color: "var(--ink-faint)", margin: "0 2px" }}>→</span>}
                    <span style={i === 0 || i === cityBreadcrumb.length - 1
                      ? { color: "var(--ink-faint)" }
                      : { color: "var(--ink-light)", fontWeight: 500 }
                    }>{city}</span>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
            {selectedIds.size > 0 && (
              <button
                className="seg-btn"
                onClick={handleBulkDelete}
                style={{ color: "#C05621", borderColor: "#C05621", whiteSpace: "nowrap" }}
              >
                Remove {selectedIds.size}
              </button>
            )}
            {onLocalRestaurants && (
              <button
                className="seg-btn"
                onClick={onLocalRestaurants}
                title="Local restaurants"
              >
                🍽
              </button>
            )}
            <button
              className="seg-btn primary"
              onClick={() => handleOpenAdd()}
              style={{ whiteSpace: "nowrap" }}
            >
              <Icon name="plus" size={13} /> Add
            </button>
          </div>
        </div>

        {/* Trip notes */}
        {(notes || editingNotes) && (
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--border)" }}>
            {editingNotes ? (
              <div>
                <textarea
                  ref={notesRef}
                  className="form-input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  style={{ resize: "vertical", fontFamily: "inherit", fontSize: 13, width: "100%", marginBottom: 8 }}
                  placeholder="Packing list, reminders, ideas..."
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <button className="seg-btn primary" onClick={handleSaveNotes} disabled={savingNotes}>
                    {savingNotes ? "Saving..." : "Save"}
                  </button>
                  <button className="seg-btn" onClick={() => { setNotes(trip.notes || ""); setEditingNotes(false); }}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div
                onClick={() => setEditingNotes(true)}
                style={{ fontSize: 13, color: "var(--ink-mid)", lineHeight: 1.5, cursor: "pointer", whiteSpace: "pre-wrap" }}
              >
                {notes}
              </div>
            )}
          </div>
        )}
        {!notes && !editingNotes && (
          <button
            onClick={() => setEditingNotes(true)}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "var(--ink-faint)", padding: "6px 0 0", display: "block" }}
          >
            + Add notes
          </button>
        )}
      </div>

      <WeatherStrip segments={visibleSegments} tripId={trip.id} />

      <main className="main" style={{ paddingBottom: 40 }}>
        {grouped.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🗺️</div>
            <h3>No segments yet</h3>
            <p>Forward a confirmation to reservations@gullivertravels.app or tap Add to enter one manually.</p>
          </div>
        ) : (
          grouped.map(([date, segs]) => {
            // Find the first date that is today or in the future
            const allDates = grouped.map(([d]) => d);
            const firstActiveDate = allDates.find(d => d >= today) || null;
            const isActive = date === firstActiveDate;
            const isToday = date === today;
            return (
            <div className="day-group" key={date} ref={isActive ? todayRef : null} style={{ scrollMarginTop: "calc(var(--header-height) + var(--trip-header-height, 0px) + var(--weather-strip-height, 0px) + 8px)" }}>
              <div className="day-label" onClick={() => handleOpenAdd(date)} style={isActive ? { color: "var(--accent)", fontWeight: 700 } : {}}>
                {formatDateFull(date)}
                {isToday && <span style={{ fontSize: 10, fontWeight: 600, color: "var(--accent)", background: "rgba(192,86,33,0.1)", borderRadius: 8, padding: "2px 7px", marginLeft: 6 }}>TODAY</span>}
                <span className="day-add">+ add</span>
              </div>
              <div className="segments-list">
                {segs.map((seg) => (
                  <SegmentCard
                    key={seg.id}
                    segment={seg}
                    tripId={trip.id}
                    isSharedTrip={isSharedTrip}
                    currentUserId={user.uid}
                    selected={selectedIds.has(seg.id)}
                    onSelect={handleToggleSelect}
                    onAddSegment={handleAddSegment}
                    savedFaves={savedFaves}
                    weatherCache={weatherCache}
                  />
                ))}
              </div>
            </div>
            );
          })
        )}
      </main>

      {/* Bulk delete dialog — only shown on shared trips */}
      {showBulkDeleteDialog && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowBulkDeleteDialog(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">Remove {selectedIds.size} segment{selectedIds.size > 1 ? "s" : ""}</div>
              <button className="modal-close" onClick={() => setShowBulkDeleteDialog(false)}>
                <Icon name="close" size={14} />
              </button>
            </div>
            <div style={{ fontSize: 14, color: "var(--ink-mid)", marginBottom: 20 }}>
              How would you like to remove these?
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                className="seg-btn"
                style={{ padding: "12px 16px", textAlign: "left", fontSize: 14 }}
                onClick={executeBulkHide}
              >
                <div style={{ fontWeight: 500, color: "var(--ink)" }}>Hide from my view</div>
                <div style={{ fontSize: 12, color: "var(--ink-light)", marginTop: 2 }}>
                  Only you won't see them — other travelers are unaffected
                </div>
              </button>
              <button
                className="seg-btn"
                style={{ padding: "12px 16px", textAlign: "left", fontSize: 14, borderColor: "#C05621" }}
                onClick={executeBulkDelete}
              >
                <div style={{ fontWeight: 500, color: "#C05621" }}>Remove for everyone</div>
                <div style={{ fontSize: 12, color: "var(--ink-light)", marginTop: 2 }}>
                  Permanently deletes these segments from the shared trip
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {showAdd && (
        <AddSegmentModal
          onClose={() => setShowAdd(false)}
          onAdd={handleAddSegment}
          segments={visibleSegments}
          prefillDate={prefillDate}
          prefillCity={prefillCity}
          tripStartDate={trip.startDate}
          tripEndDate={trip.endDate}
          tripId={trip.id}
        />
      )}

      {showShare && (
        <ShareModal
          trip={trip}
          user={user}
          onClose={() => setShowShare(false)}
        />
      )}

      {showExport && (
        <ExportModal
          trip={tripData}
          segments={visibleSegments}
          onClose={() => setShowExport(false)}
          user={user}
        />
      )}

      {showEditTrip && (
        <EditTripModal
          trip={tripData}
          onClose={() => setShowEditTrip(false)}
          onUpdated={(updated) => setTripData(updated)}
        />
      )}

      {showMerge && (
        <MergeTripModal
          trip={tripData}
          user={user}
          onClose={() => setShowMerge(false)}
          onMerged={() => {
            setShowMerge(false);
          }}
        />
      )}
    </>
  );
}
